import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CLOSE from '../assets/close.svg';
import Add from '../assets/add.svg';
import Title from '../assets/title.svg';
import Loader from '../../../components/atomic/Loader';
import { createStandardQuestion, updateStandardQuestion, fetchSQByTaskId } from '../../../slice/sqSlice';
import { selectSelectedTask } from '../../../slice/taskSlice';
import { Loader2Icon } from 'lucide-react';

const AddQuestionModal = ({ isOpen, onClose, selectedQuestion }) => {

    const { data } = useSelector((state) => state.admin);

    const isAdmin =
      data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
    const isSuperAdmin =  data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
    const userOrganizationId = data?.organization_id;
    const dispatch = useDispatch();
    const selectedTask = useSelector(selectSelectedTask);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        synonyms: []
    });
    const [currentSynonym, setCurrentSynonym] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (selectedQuestion) {
            setFormData({
                title: selectedQuestion.title || '',
                description: selectedQuestion.description || '',
                synonyms: selectedQuestion.synonyms || []
            });
        } else {
            clearForm();
        }
    }, [selectedQuestion]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({

            ...prev,
            [name]: value
        }));
    };

    const addSynonym = (value) => {
        if (value.trim() && !formData.synonyms.includes(value.trim())) {
            setFormData(prev => ({

                ...prev,
                synonyms: [...prev.synonyms, value.trim()]
            }));
        }
        setCurrentSynonym('');
    };

    const removeSynonym = (synonym) => {
        setFormData(prev => ({

            ...prev,
            synonyms: prev.synonyms.filter(s => s !== synonym)
        }));
    };

    const clearForm = () => {
        setFormData({
            title: '',
            description: '',
            synonyms: []
        });
        setCurrentSynonym('');
        setError('');
    };

    const handleSubmit = async () => {
        if (!formData.title.trim() || !formData.description.trim()) {
            setError('Bitte füllen Sie alle Pflichtfelder aus.');
            return;
        }

        // Check permissions for ADMIN users
        if (isAdmin && !isSuperAdmin) {
            // ADMIN users can only edit questions for tasks assigned to their organization
            if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
                setError('Sie können nur Fragen für Aufgaben Ihrer Organisation bearbeiten');
                return;
            }
        }

        setIsLoading(true);
        setError('');

        try {
            if (selectedQuestion) {
                // Update existing question
                await dispatch(updateStandardQuestion({
                    id: selectedQuestion.id,
                    ...formData
                })).unwrap();
            } else {
                // Create new question
                await dispatch(createStandardQuestion({
                    ...formData,
                    task_id: selectedTask.id
                })).unwrap();
                
                // Clear form fields after successful creation
                setFormData({
                    title: '',
                    description: '',
                    synonyms: []
                });
                setCurrentSynonym('');
            }
            
            // Refetch questions to update the list
            await dispatch(fetchSQByTaskId(selectedTask.id));
            
            onClose();
        } catch (err) {
            setError(err.message || 'Ein Fehler ist aufgetreten');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl text-white font-semibold">{selectedQuestion ? 'Update hinzufügen' : 'Frage hinzufügen'}</h2>
                    <button onClick={() => { clearForm(); onClose(); }} className="p-1">
                        <img src={CLOSE} alt="close" className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 text-red-600">
                        {error}
                    </div>
                )}

                {/* Title Input */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Titel der Frage</label>
                    <div className="relative">
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            disabled={!isAdmin}
                            placeholder="Titel eingeben"
                            className="w-full p-2 pl-10 border border-gray-200 rounded-lg focus:outline-none focus:border-custom-yellow"
                        />
                        <img
                            src={Title}
                            alt="title"
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                        />
                    </div>
                </div>

                {/* Question Input */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Frage</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Frage eingeben"
                        disabled={!isAdmin}
                        rows={4}
                        className="w-full p-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-custom-yellow"
                    />
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-4">
                    <button
                        onClick={() => { clearForm(); onClose(); }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-lg text-gray-600 hover:text-gray-800"
                    >
                        <img src={CLOSE} alt="" />
                        Abbrechen
                    </button>
                    <button
                        disabled={!isAdmin || isLoading}
                        onClick={handleSubmit}
                        className={`flex items-center gap-2 px-4 py-2 bg-custom-yellow rounded-lg font-bold min-w-[150px] justify-center ${!isAdmin || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isLoading ? (
                            <>
                                <div className="w-5 h-5">
                                    <Loader2Icon className="animate-spin" />
                                </div>
                                <span>Speichern...</span>
                            </>
                        ) : (
                            <>
                                <img src={Add} alt="" />
                                <span className='text-white'>{selectedQuestion ? 'Update hinzufügen' : 'Frage hinzufügen'}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddQuestionModal;
